// User culture: display language and calendar in their time zone.
// Pure and deterministic: the environment (browser languages, detected time zone) is injected by the caller.
// Intl is part of the language: the same computation gives the same result in the browser and on the server.

export const LANGS = ['en', 'fr'] as const;
export type Lang = (typeof LANGS)[number];
/** Source language of the game: core texts are written in this language, and it is the fallback when the device reports none. */
export const SOURCE_LANG: Lang = 'en';
export type ClockPref = 'auto' | 'h12' | 'h23';

/** Preferences saved in the profile (synchronised with the account). */
export interface CulturePrefs {
  lang: 'auto' | Lang;
  /** Concrete IANA time zone, even in automatic mode: the server uses it for days and weeks. */
  timeZone: string;
  /** True: the time zone follows the device's at every launch. */
  autoTimeZone: boolean;
  clock: ClockPref;
}
export interface CultureEnv { languages: readonly string[]; timeZone: string }
export interface Culture { lang: Lang; locale: string; timeZone: string; hour12: boolean | undefined }

export const DEFAULT_CULTURE: CulturePrefs = { lang: 'auto', timeZone: 'UTC', autoTimeZone: true, clock: 'auto' };

const tzCache = new Map<string, Intl.DateTimeFormat | null>();
function partsFormat(tz: string): Intl.DateTimeFormat | null {
  if (!tzCache.has(tz)) {
    let f: Intl.DateTimeFormat | null;
    try { f = new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' }); } catch { f = null; }
    tzCache.set(tz, f);
  }
  return tzCache.get(tz)!;
}

/** True if the IANA time zone is known to the JavaScript engine. */
export const isValidTimeZone = (tz: string): boolean => typeof tz === 'string' && tz.length > 0 && tz.length <= 64 && partsFormat(tz) !== null;

/** Supported language closest to the device preferences (English for an unknown or missing language). */
export function matchLang(languages: readonly string[]): Lang {
  for (const l of languages) {
    const base = String(l).toLowerCase().split('-')[0];
    if ((LANGS as readonly string[]).includes(base)) return base as Lang;
  }
  return SOURCE_LANG;
}

/** Resolves preferences into a concrete culture (language, regional locale, time zone, clock). */
export function resolveCulture(prefs: Partial<CulturePrefs> | undefined, env: CultureEnv): Culture {
  const p = { ...DEFAULT_CULTURE, ...prefs };
  const lang = p.lang === 'auto' || !(LANGS as readonly string[]).includes(p.lang) ? matchLang(env.languages) : p.lang;
  // Keeps the device region when it matches the chosen language (fr-BE, en-GB…): local date formats.
  const regional = env.languages.find(l => String(l).toLowerCase().split('-')[0] === lang);
  let locale: string = lang;
  if (regional) { try { locale = Intl.getCanonicalLocales(regional)[0] ?? lang; } catch { locale = lang; } }
  const wanted = p.autoTimeZone ? env.timeZone : p.timeZone;
  const timeZone = isValidTimeZone(wanted) ? wanted : isValidTimeZone(env.timeZone) ? env.timeZone : 'UTC';
  return { lang, locale, timeZone, hour12: p.clock === 'auto' ? undefined : p.clock === 'h12' };
}

// ---------- Calendar in a time zone ----------
interface Parts { y: number; m: number; d: number; h: number; mi: number; s: number }
function zonedParts(ts: number, tz: string): Parts {
  const f = partsFormat(tz) ?? partsFormat('UTC')!;
  const o: Record<string, number> = {};
  for (const p of f.formatToParts(new Date(ts))) if (p.type !== 'literal') o[p.type] = +p.value;
  return { y: o.year, m: o.month, d: o.day, h: o.hour === 24 ? 0 : o.hour, mi: o.minute, s: o.second };
}

/** Offset of the time zone from UTC at the given instant, in milliseconds. */
export function tzOffset(ts: number, tz: string): number {
  const p = zonedParts(ts, tz);
  return Date.UTC(p.y, p.m - 1, p.d, p.h, p.mi, p.s) - Math.floor(ts / 1000) * 1000;
}

/** Instant matching a wall-clock date in the time zone (robust to daylight-saving changes). */
function fromWall(y: number, m: number, d: number, tz: string): number {
  const wall = Date.UTC(y, m - 1, d);
  let ts = wall - tzOffset(wall, tz);
  ts = wall - tzOffset(ts, tz);
  return ts;
}

/** Midnight (start of the calendar day) in the time zone; without a zone, that of the runtime environment. */
export function startOfDay(ts: number, tz?: string): number {
  if (!tz) { const d = new Date(ts); d.setHours(0, 0, 0, 0); return d.getTime(); }
  const p = zonedParts(ts, tz);
  return fromWall(p.y, p.m, p.d, tz);
}

/** Adds n calendar days and returns the start of that day. */
export function addDays(ts: number, n: number, tz?: string): number {
  if (!tz) { const d = new Date(ts); d.setDate(d.getDate() + n); d.setHours(0, 0, 0, 0); return d.getTime(); }
  const p = zonedParts(ts, tz);
  const shifted = new Date(Date.UTC(p.y, p.m - 1, p.d + n));
  return fromWall(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate(), tz);
}

/** Day of the week in the time zone: 0 = Sunday … 6 = Saturday. */
export function weekday(ts: number, tz?: string): number {
  if (!tz) return new Date(ts).getDay();
  const p = zonedParts(ts, tz);
  return new Date(Date.UTC(p.y, p.m - 1, p.d)).getUTCDay();
}

/** Start of the week (Monday by default, ISO) in the time zone. */
export function startOfWeek(ts: number, tz?: string, firstDay = 1): number {
  return addDays(ts, -((weekday(ts, tz) - firstDay + 7) % 7), tz);
}

/** Number of calendar days between two instants (positive if `b` is after `a`). */
export function daysBetween(a: number, b: number, tz?: string): number {
  // A day lasts 23 to 25 h depending on daylight-saving changes: rounding absorbs the gap.
  return Math.round((startOfDay(b, tz) - startOfDay(a, tz)) / 86400000);
}
